import { useMemo } from 'react';
import {
  LayersNodes,
  RelationSection,
} from '@components/MainStage/ContextMenu/RelationshipsMenu/constants/types';
import {
  generateLayersNodes,
  getChildSubLayer,
  getParentSubLayers,
  getSectionTitle,
} from '@components/MainStage/ContextMenu/RelationshipsMenu/helpers';
import { NodesTypes } from '@constants/canvas/general';
import { SubLayerTypes } from '@constants/canvas/layers';
import { useProject } from '@context/Project';
import { canvasMap } from '@utils/canvas/CanvasMap';

type Options = {
  nodeId: string;
};

const useRelationSections = ({ nodeId }: Options): RelationSection[] => {
  const {
    toolbox: { mode },
  } = useProject();

  const node = canvasMap.nodeMap.get(nodeId);
  const currentNode = node?.type === NodesTypes.CustomNode ? node : undefined;

  const { parentSubLayers, childSubLayers } = useMemo(() => {
    if (!currentNode || !mode)
      return { parentSubLayers: [], childSubLayers: [] };

    const currentSubLayer = currentNode.parentNode as SubLayerTypes;

    const { parentSubLayers } = getParentSubLayers(mode, currentSubLayer);
    const { childSubLayers } = getChildSubLayer(mode, currentSubLayer);

    return { parentSubLayers, childSubLayers };
  }, [mode, currentNode]);

  const layersRelatedNodes = useMemo<LayersNodes>(() => {
    if (!currentNode) return {};

    const {
      data: { dto },
    } = currentNode;

    const parentLayersNodes = generateLayersNodes(
      dto.parentsIds ?? [],
      parentSubLayers,
    );

    const childrenLayersNodes = generateLayersNodes(
      dto.childrenIds ?? [],
      childSubLayers,
    );

    return { ...parentLayersNodes, ...childrenLayersNodes };
  }, [parentSubLayers, childSubLayers]);

  return useMemo(() => {
    if (!mode) return [];

    return [...parentSubLayers, ...childSubLayers].reduce<RelationSection[]>(
      (acc, layer) => {
        const relatedNodes = layersRelatedNodes[layer] ?? [];

        if (relatedNodes.length > 0) {
          acc.push({ title: getSectionTitle(mode, layer), relatedNodes });
        }

        return acc;
      },
      [],
    );
  }, [layersRelatedNodes]);
};

export default useRelationSections;
